import React, { useState, useCallback, useEffect, useRef } from "react";
import { Loader2, UserX } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/AuthContext";
import { fetchBlockedUsers, unblockUser } from "../../services/blockService";
import ErrorModal from "../Modal/ErrorModal";

const useIntersectionObserver = (callback, options = {}) => {
  const targetRef = useRef(null);

  useEffect(() => {
    const target = targetRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) callback();
      },
      { threshold: 0.1, rootMargin: "100px", ...options }
    );

    observer.observe(target);
    return () => {
      if (target) observer.unobserve(target);
    };
  }, [callback, options]);

  return targetRef;
};

const BlockedAccountsSection = () => {
  const { apiCall } = useAuth();
  const queryClient = useQueryClient();

  const [blockedUsers, setBlockedUsers] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [unblockingId, setUnblockingId] = useState(null);
  const [errorModal, setErrorModal] = useState({ isOpen: false, message: "" });

  // Initial load
  useEffect(() => {
    let cancelled = false;

    const loadInitial = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const result = await fetchBlockedUsers(apiCall, null);
        if (cancelled) return;
        setBlockedUsers(result.blockedUsers);
        setCursor(result.nextCursor);
        setHasMore(result.hasMore);
      } catch (err) {
        if (!cancelled) setError("Failed to load blocked accounts");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    loadInitial();
    return () => {
      cancelled = true;
    };
  }, [apiCall]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || isLoadingMore || isLoading) return;

    setIsLoadingMore(true);
    try {
      const result = await fetchBlockedUsers(apiCall, cursor);
      setBlockedUsers((prev) => {
        const existingIds = new Set(prev.map((u) => u.blockId));
        const newOnes = result.blockedUsers.filter(
          (u) => !existingIds.has(u.blockId)
        );
        return [...prev, ...newOnes];
      });
      setCursor(result.nextCursor);
      setHasMore(result.hasMore);
    } catch (err) {
      // Silent fail on "load more" — the list already shown stays intact
    } finally {
      setIsLoadingMore(false);
    }
  }, [apiCall, cursor, hasMore, isLoadingMore, isLoading]);

  const loadMoreRef = useIntersectionObserver(handleLoadMore);

  const handleUnblock = useCallback(
    async (profileID) => {
      setUnblockingId(profileID);

      const result = await unblockUser(apiCall, profileID);

      setUnblockingId(null);

      if (!result.success) {
        setErrorModal({
          isOpen: true,
          message: result.error || "Failed to unblock user. Please try again.",
        });
        return;
      }

      // Remove it straight from local state — the record is gone.
      setBlockedUsers((prev) => prev.filter((u) => u.profileID !== profileID));

      queryClient.invalidateQueries({ queryKey: ["explorePosts"] });
      queryClient.invalidateQueries({ queryKey: ["trendingPosts"] });
      queryClient.invalidateQueries({ queryKey: ["suggestedUsers"] });
    },
    [apiCall, queryClient]
  );

  return (
    <div className="bg-white min-h-screen sm:min-h-0">
      <div className="px-4 sm:px-0">
        {isLoading && blockedUsers.length === 0 ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : error && blockedUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-sm text-red-600 font-medium">{error}</p>
          </div>
        ) : blockedUsers.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="w-16 h-16 rounded-full border-2 border-black flex items-center justify-center mb-3">
              <UserX className="w-8 h-8 text-black" />
            </div>
            <p className="text-sm font-medium text-gray-900 mb-1">
              No blocked accounts
            </p>
            <p className="text-xs text-gray-500 max-w-xs">
              Accounts you block will show up here
            </p>
          </div>
        ) : (
          <>
            {blockedUsers.map((user) => (
              <div
                key={user.blockId}
                className="flex items-center justify-between py-3 border-b border-gray-100 last:border-b-0"
              >
                <div className="flex items-center gap-3">
                  {user.profilePicture?.small || user.profilePicture?.medium ? (
                    <img
                      src={user.profilePicture.small || user.profilePicture.medium}
                      alt={user.username}
                      className="w-10 h-10 rounded-full object-cover border-[0.5px] border-gray-200"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center overflow-hidden">
                      <svg
                        className="w-full h-full text-slate-400"
                        viewBox="0 0 100 100"
                        fill="currentColor"
                      >
                        <circle cx="50" cy="35" r="18" />
                        <path d="M50 58 C 25 58, 10 65, 10 100 L 90 100 C 90 65, 75 58, 50 58 Z" />
                      </svg>
                    </div>
                  )}
                  <span className="text-sm font-semibold text-black">
                    {user.username}
                  </span>
                </div>

                <button
                  onClick={() => handleUnblock(user.profileID)}
                  disabled={unblockingId === user.profileID}
                  style={{
                    padding: "8px 16px",
                    backgroundColor: "#b0d284",
                    color: "black",
                    fontSize: "14px",
                    fontWeight: "600",
                    borderRadius: "8px",
                    border: "none",
                    cursor:
                      unblockingId === user.profileID
                        ? "not-allowed"
                        : "pointer",
                    opacity: unblockingId === user.profileID ? 0.6 : 1,
                  }}
                >
                  {unblockingId === user.profileID ? "Unblocking..." : "Unblock"}
                </button>
              </div>
            ))}

            {hasMore && (
              <div ref={loadMoreRef} className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
              </div>
            )}
          </>
        )}
      </div>

      <ErrorModal
        isOpen={errorModal.isOpen}
        message={errorModal.message}
        onClose={() => setErrorModal({ isOpen: false, message: "" })}
      />
    </div>
  );
};

export default BlockedAccountsSection;